import { useEffect, type HTMLAttributes, type ReactNode } from "react";
import styles from "./BottomSheet.module.css";
import { cx } from "../cx";
import { BrickPill } from "./BrickPill";

export type BottomSheetHeaderProps = {
  title?: ReactNode;
  description?: ReactNode;
  brickCount?: number | string;
  className?: string;
};

/** 바텀시트 상단 (Figma 97:3829): 핸들 + 제목/설명 + 보유 브릭(숫자만, F04-07). */
export function BottomSheetHeader({ title, description, brickCount, className }: BottomSheetHeaderProps) {
  return (
    <div className={cx(styles.header, className)}>
      <div className={styles.handle} />
      {(title || description || brickCount !== undefined) && (
        <div className={styles.headerRow}>
          <div className={styles.texts}>
            {title && <p className={styles.title}>{title}</p>}
            {description && <p className={styles.description}>{description}</p>}
          </div>
          {brickCount !== undefined && <BrickPill size="small" count={brickCount} />}
        </div>
      )}
    </div>
  );
}

export type BottomSheetProps = HTMLAttributes<HTMLDivElement> & {
  open: boolean;
  onClose: () => void;
  header?: BottomSheetHeaderProps;
  children: ReactNode;
  /** 스크롤 영역 밖 하단 고정 영역(보통 BottomCTA fixed={false}). 본문과 간격 16 */
  footer?: ReactNode;
};

/** 바텀시트 컨테이너: 배경 --hamlak-sheet-bg, 상단 radius 20, 하단 safe area. 본문은 스크롤, footer 는 스크롤 밖. */
export function BottomSheet({ open, onClose, header, className, children, footer, ...rest }: BottomSheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div role="dialog" aria-modal="true" className={cx(styles.sheet, className)} onClick={(e) => e.stopPropagation()} {...rest}>
        <BottomSheetHeader {...(header ?? {})} />
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
}
